import { useTranslation } from 'react-i18next'

import { selectAllClients } from '@/features/clients/clientsSelectors'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import type { ApiInvite } from '@/features/users/types'
import { resendInvite, revokeInvite, invitesRetried, fetchInvites } from '@/features/users/usersSlice'
import {
  selectInvitesCache,
  selectInvitesError,
  selectInviteList
} from '@/features/users/usersSelectors'
import { formatDate } from '@/lib/format'
import { useAppDispatch, useAppSelector } from '@/store'

const columnHeadClass =
  'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'


/**
 * The pending-invites table (PX-W6.2): server rows with resend + revoke
 * actions, each confirmed before it fires.
 */
export function PendingInvitesTable() {
  const { t, i18n } = useTranslation(['peopleAdmin', 'nav', 'projectWorkspace'])
  const dispatch = useAppDispatch()

  const rows = useAppSelector(selectInviteList)
  const cache = useAppSelector(selectInvitesCache)
  const error = useAppSelector(selectInvitesError)
  const clients = useAppSelector(selectAllClients)

  /** The binding an invite carries: the project (with its access) and/or the client it came for. */
  const bindingLabels = (row: ApiInvite): string[] => {
    const labels: string[] = []
    if (row.projectName !== undefined && row.projectName !== '') {
      const access = row.projectAccessLevel === undefined ? '' : ` (${t(`projectWorkspace:members.access.${row.projectAccessLevel}`)})`
      labels.push(`${t('invites.binding.project', { name: row.projectName })}${access}`)
    }
    if (row.clientId !== undefined && row.clientId !== '') {
      const name = clients.find((client) => client.id === row.clientId)?.name ?? row.clientId
      labels.push(t('invites.binding.client', { name }))
    }
    return labels
  }

  const onResendClicked = (row: { id: string; email: string }): void => {
    if (!window.confirm(t('invites.resendConfirm', { email: row.email }))) return
    void dispatch(resendInvite(row.id))
  }

  const onRevokeClicked = (row: { id: string; email: string }): void => {
    if (!window.confirm(t('invites.revokeConfirm', { email: row.email }))) return
    void dispatch(revokeInvite(row.id))
  }

  const onRetryClicked = (): void => {
    dispatch(invitesRetried())
    void dispatch(fetchInvites())
  }

  return (
    <section
      aria-labelledby="pending-invites-label"
      className="rounded-lg border border-border bg-card px-4 py-4"
    >
      <div id="pending-invites-label">
        <SectionLabel>{t('invites.title')}</SectionLabel>
      </div>
      <div className="mt-3">
        {cache.loading ? (
          <LoadingPanel />
        ) : error !== null && error.status === 403 ? (
          <ForbiddenPanel requested="/{orgId}/users/invites" />
        ) : error !== null ? (
          <ErrorPanel error={error} onRetryClicked={onRetryClicked} />
        ) : rows.length === 0 ? (
          <EmptyPanel body={t('invites.empty')} />
        ) : (
          <Table>
            <TableCaption className="sr-only">{t('invites.title')}</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead className={columnHeadClass}>{t('invites.columns.email')}</TableHead>
                <TableHead className={columnHeadClass}>{t('invites.columns.role')}</TableHead>
                <TableHead className={columnHeadClass}>{t('invites.columns.binding')}</TableHead>
                <TableHead className={columnHeadClass}>{t('invites.columns.language')}</TableHead>
                <TableHead className={columnHeadClass}>{t('invites.columns.inviter')}</TableHead>
                <TableHead className={columnHeadClass}>{t('invites.columns.expires')}</TableHead>
                <TableHead className={columnHeadClass}>{t('invites.columns.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const binding = bindingLabels(row)
                return (
                  <TableRow key={row.id}>
                    <TableCell className="py-2.5 font-medium text-foreground">{row.email}</TableCell>
                    <TableCell>
                      {row.role !== undefined && row.role !== '' ? (
                        // The role's label, never the raw wire value (fix wave 1, VL2).
                        <StatusPill tone="proposed">
                          {t(`peopleAdmin:roles.${row.role.toLowerCase()}`, { defaultValue: row.role })}
                        </StatusPill>
                      ) : (
                        <StatusPill tone="neutral">{t('invites.defaultRole')}</StatusPill>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-ink-soft">
                      {binding.length === 0 ? '—' : binding.map((label) => <span key={label} className="block">{label}</span>)}
                    </TableCell>
                    <TableCell className="text-sm text-ink-soft">
                      {row.locale === undefined ? (
                        '—'
                      ) : (
                        <abbr title={t(`nav:language.${row.locale}`)}>{row.locale.toUpperCase()}</abbr>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-ink-soft">
                      {row.inviterName ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm tabular-nums text-foreground">
                      {formatDate(row.expiresAt, i18n.resolvedLanguage) ?? '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => onResendClicked(row)}
                          className="rounded-md border border-input px-2 py-1 text-xs text-ink-soft hover:bg-muted max-sm:min-h-[44px]"
                        >
                          {t('invites.resend')}
                        </button>
                        <button
                          type="button"
                          onClick={() => onRevokeClicked(row)}
                          className="rounded-md border border-input px-2 py-1 text-xs text-blocked hover:bg-muted max-sm:min-h-[44px]"
                        >
                          {t('invites.revoke')}
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </section>
  )
}
